/**
 * The locale this browser last resolved, remembered across reloads (POS2-1133).
 *
 * Both rungs above the browser in `useLocale`'s chain are queries that start `undefined`, so
 * without this the app resolves `navigator.language` on every boot and corrects itself once the
 * reads land. Two consequences, both of them the user's problem rather than a detail:
 *
 *   * A German browser paints German for a user who chose English — fetching the German chunk and
 *     the German overrides on the way — and then flips back. Every load.
 *   * Offline, where neither read can answer at all, a German user gets whatever their browser
 *     says. The catalogue is precached and German would render; nothing was asking for it.
 *
 * Deliberately the *resolved* locale rather than the stored preference, because the organisation
 * default is a rung too and it is the one a German rollout leans on. It is a seed, never an
 * answer: a successful preference read always replaces it, so it cannot be stale for longer than
 * the first paint while online.
 *
 * Cleared on sign-out beside the org and site stores, for the reason POS2-1068 cleared those —
 * the next person to sign in on this browser must not inherit it.
 */
import { isAppLocale, type Locale } from "./config";

const STORAGE_KEY = "foodsight:locale";

export function readLastLocale(): Locale | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored && isAppLocale(stored) ? stored : null;
  } catch {
    return null;
  }
}

/** Remember the resolved locale, or forget it — `null` on sign-out. */
export function setLastLocale(locale: Locale | null): void {
  try {
    if (locale) localStorage.setItem(STORAGE_KEY, locale);
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage can be unavailable: private mode, a quota, a locked-down browser. The chain still
    // resolves without it — it just resolves a paint later, which is where this started.
  }
}
